import AddIcon from "@mui/icons-material/Add";
import { Box, IconButton } from "@mui/material";
import { type FC, type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useJobs } from "@/hooks/useJobs";
import { RATIO_LADDER_UNBOUNDED_CENSUS_END } from "@/utils/censusResolution";
import type { NurseRole, RatioLadder } from "@/utils/models";
import { LADDER_REORDER_DELAY_MS, type RatioLadderRowProps } from "./consts";
import { LadderStepSlide, ladderOrderKey } from "./LadderStepSlide";
import { RatioLadderRowV1 } from "./RatioLadderRowV1";
import { addLadderRow, canAddLadderRow, getConflictingDaysByRow, hasValidAmount, hasValidRange, rowDays, setLadderRowMax } from "./ratioLadderCoverage";
import { useSettledLadderOrder } from "./useSettledLadderOrder";

/**
 * What the admin typed into Max. Only the ∞ button sets "no upper limit"; typing replaces the "∞" shown
 * in the field, so anything but digits is dropped. Blank stays blank (NaN) and blocks save.
 */
export const parseTypedMax = (typed: string): number => {
	const digits = typed.replace(/\D/g, "");
	return digits === "" ? Number.NaN : Number.parseInt(digits, 10);
};

/** What a whole-number field's text means: its number, or NaN for blank or non-numeric text, which blocks save. */
export const parseTypedWholeNumber = (typed: string): number => Number.parseInt(typed, 10);

type RatioLadderSectionCommonProps = {
	ratioLadder: RatioLadder[];
	onRatioLadderChange: (ratioLadder: RatioLadder[]) => void;
	locationRoles: NurseRole[];
	locationJobId: string;
	jobManagementEnabled: boolean;
};

/**
 * The Schedule Template's rung list; its host renders the title and the gap message around it. The Locations page's
 * ladders by day are `RatioLadderSectionV2`, which its callers render directly.
 */
export type RatioLadderSectionProps = RatioLadderSectionCommonProps & { design: "v1" };

export const RatioLadderSection: FC<RatioLadderSectionProps> = (props): ReactElement => {
	const { ratioLadder, onRatioLadderChange, locationRoles, locationJobId, jobManagementEnabled } = props;
	const { t } = useTranslation();
	const enabledJobs = useJobs();
	const showRoleChips = jobManagementEnabled ? false : locationRoles.length > 1;
	const showJobChips = jobManagementEnabled && enabledJobs.length > 1;
	const conflictingDaysByRow = getConflictingDaysByRow(ratioLadder);
	const [focusedNewRowId, setFocusedNewRowId] = useState<string | null>(null);

	const handleAddRatio = () => {
		const { ladder, newRowId } = addLadderRow(ratioLadder, { roles: locationRoles, jobId: locationJobId });
		setFocusedNewRowId(newRowId);
		onRatioLadderChange(ladder);
	};

	// The ladder as it was when a Max field took focus. Every keystroke in that field is applied to it, so
	// the rows that follow keep their place while the admin clears and retypes (see `setLadderRowMax`).
	const maxEditStart = useRef<{ rowId: string; ladder: RatioLadder[] } | null>(null);
	const handleSetMax = (row: RatioLadder, max: number) => {
		const start = maxEditStart.current;
		onRatioLadderChange(setLadderRowMax(start?.rowId === row.id ? start.ladder : ratioLadder, row.id, max));
	};

	const handleUpdateRatio = (id: string, updates: Partial<RatioLadder>) => {
		const newRatios = ratioLadder.map((ratio) => (ratio.id === id ? { ...ratio, ...updates } : ratio));
		onRatioLadderChange(newRatios);
	};

	const handleDeleteRatio = (id: string) => {
		const newRatios = ratioLadder.filter((ratio) => ratio.id !== id);
		onRatioLadderChange(newRatios);
	};

	/** Everything one rung's markup needs: its flags, and every handler bound to it. */
	const rowPropsOf = (ratio: RatioLadder, index: number): RatioLadderRowProps => {
		const isLastRow = index === ratioLadder.length - 1;
		const conflictingDays = conflictingDaysByRow.get(ratio.id);
		const foundJob = enabledJobs.find((j) => j.id === ratio.jobId);
		return {
			ratio,
			locationRoles,
			showRoleChips,
			showJobChips,
			jobDisplayName: foundJob?.displayName,
			hasRolesError: showRoleChips && ratio.roles.length === 0,
			hasJobsError: showJobChips && !ratio.jobId,
			hasAmountError: !hasValidAmount(ratio),
			hasDaysError: rowDays(ratio).length === 0,
			hasRangeError: !hasValidRange(ratio),
			hasCensusConflict: !!conflictingDays,
			conflictingDays,
			isOpenEnded: ratio.censusEnd === RATIO_LADDER_UNBOUNDED_CENSUS_END,
			autoFocusMax: ratio.id === focusedNewRowId,
			showAddButton: isLastRow && canAddLadderRow(ratioLadder),
			showDivider: index < ratioLadder.length - 1,
			stepNumber: index + 1,
			onUpdate: (updates) => handleUpdateRatio(ratio.id, updates),
			onMinTyped: (typed) => handleUpdateRatio(ratio.id, { censusStart: parseTypedWholeNumber(typed) }),
			onAmountTyped: (typed) => handleUpdateRatio(ratio.id, { amount: parseTypedWholeNumber(typed) }),
			onMaxTyped: (typed) => handleSetMax(ratio, parseTypedMax(typed)),
			onMaxUnbounded: () => handleSetMax(ratio, RATIO_LADDER_UNBOUNDED_CENSUS_END),
			onMaxFocus: () => {
				maxEditStart.current = { rowId: ratio.id, ladder: ratioLadder };
			},
			onMaxBlur: () => {
				maxEditStart.current = null;
			},
			onDelete: () => handleDeleteRatio(ratio.id),
			onAdd: handleAddRatio,
		};
	};

	// Sorted by "Census from", but re-sorted only after a pause in editing, so a step doesn't jump while it's typed in.
	const settledLadder = useSettledLadderOrder(ratioLadder, LADDER_REORDER_DELAY_MS);
	const settledOrderKey = ladderOrderKey(settledLadder);

	switch (props.design) {
		case "v1":
			return (
				<Box
					onClick={(e) => e.stopPropagation()}
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						width: "100%",
						alignItems: "start",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
							width: "100%",
						}}
					>
						{settledLadder.map((ratio, index) => (
							<LadderStepSlide key={ratio.id} orderKey={settledOrderKey}>
								<RatioLadderRowV1 {...rowPropsOf(ratio, index)} />
							</LadderStepSlide>
						))}
						{ratioLadder.length === 0 && (
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
								<IconButton size="small" aria-label={t("schedule.template.ratio.ladder.row.add")} onClick={handleAddRatio}>
									<AddIcon fontSize="small" />
								</IconButton>
							</Box>
						)}
					</Box>
				</Box>
			);
	}
};
